import { useEffect, useRef, useState } from "react";
import location_icon from "../../../assets/icons/location.svg";
import { buscarEnderecos, ErroGeocodificacao } from "../../../utils/geocodificacao";

const ATRASO_MS = 600; // espera a pessoa parar de digitar: o servico limita 1 req/s
const DICA_MAPA = "Você também pode clicar no mapa para marcar o ponto.";

function mensagemDoErro(e) {
  if (e instanceof ErroGeocodificacao && e.tipo === "limite") {
    return `O serviço de endereços está ocupado agora. ${DICA_MAPA}`;
  }
  return `Não foi possível buscar o endereço agora. ${DICA_MAPA}`;
}

// Campo de endereco com sugestoes (Nominatim/OpenStreetMap). Substitui o
// react-places-autocomplete, que dependia do Google Places.
//
//  value     texto do campo (controlado pelo pai)
//  onChange  (texto) a cada digitacao
//  onSelect  (resultado) quando a pessoa escolhe uma sugestao
function BuscaEndereco({ value, onChange, onSelect, disabled, className, placeholder, id = "endereco", name = "endereco" }) {
  const [sugestoes, setSugestoes] = useState([]);
  const [aberto, setAberto] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [aviso, setAviso] = useState("");
  const [ativo, setAtivo] = useState(-1);

  const raiz = useRef(null);
  const temporizador = useRef(null);
  const controlador = useRef(null);

  const cancelar = () => {
    clearTimeout(temporizador.current);
    controlador.current?.abort();
  };

  useEffect(() => cancelar, []);

  // Fecha ao clicar fora.
  useEffect(() => {
    const fora = (e) => { if (raiz.current && !raiz.current.contains(e.target)) setAberto(false); };
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, []);

  async function executar(texto) {
    controlador.current?.abort();
    const controle = new AbortController();
    controlador.current = controle;
    setCarregando(true);
    setAviso("");
    try {
      const resultados = await buscarEnderecos(texto, { signal: controle.signal });
      if (controle.signal.aborted) return;
      setSugestoes(resultados);
      setAtivo(-1);
      setAviso(resultados.length === 0 ? `Nenhum endereço encontrado. ${DICA_MAPA}` : "");
      setAberto(true);
    } catch (e) {
      if (e?.name === "AbortError" || controle.signal.aborted) return;
      setSugestoes([]);
      setAviso(mensagemDoErro(e));
      setAberto(true);
    } finally {
      if (!controle.signal.aborted) setCarregando(false);
    }
  }

  function aoDigitar(e) {
    const texto = e.target.value;
    onChange(texto);
    cancelar();
    if (texto.trim().length < 3) {
      setSugestoes([]);
      setAviso("");
      setCarregando(false);
      setAberto(false);
      return;
    }
    temporizador.current = setTimeout(() => executar(texto), ATRASO_MS);
  }

  function escolher(item) {
    cancelar();
    setAberto(false);
    setCarregando(false);
    setSugestoes([]);
    onSelect(item);
  }

  function aoTeclar(e) {
    if (e.key === "Enter") {
      // Enter nao pode enviar o formulario inteiro com o endereco pela metade.
      e.preventDefault();
      if (aberto && ativo >= 0 && sugestoes[ativo]) return escolher(sugestoes[ativo]);
      if ((value || "").trim().length >= 3) { cancelar(); executar(value); }
    } else if (e.key === "ArrowDown" && sugestoes.length) {
      e.preventDefault();
      setAberto(true);
      setAtivo((a) => (a + 1) % sugestoes.length);
    } else if (e.key === "ArrowUp" && sugestoes.length) {
      e.preventDefault();
      setAtivo((a) => (a <= 0 ? sugestoes.length - 1 : a - 1));
    } else if (e.key === "Escape") {
      setAberto(false);
    }
  }

  const mostrarLista = aberto && (carregando || sugestoes.length > 0 || aviso);

  // Perder o foco para fora do componente fecha a lista. O clique no MAPA nao
  // chega ao listener de `mousedown` do document (o Leaflet consome o evento),
  // e sem isto o aviso ("Nao foi possivel buscar...") ficava aberto por cima do
  // campo de especie, escondendo-o.
  function aoPerderFoco(e) {
    if (!raiz.current?.contains(e.relatedTarget)) setAberto(false);
  }

  return (
    <div ref={raiz} onBlur={aoPerderFoco}>
      <input
        id={id}
        name={name}
        type="text"
        autoComplete="off"
        required
        role="combobox"
        aria-expanded={!!mostrarLista}
        aria-autocomplete="list"
        aria-controls={`${id}-sugestoes`}
        aria-activedescendant={ativo >= 0 ? `${id}-sugestao-${ativo}` : undefined}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
        value={value || ""}
        onChange={aoDigitar}
        onKeyDown={aoTeclar}
      />

      {mostrarLista && (
        <div className="autocomplete-dropdown-container" id={`${id}-sugestoes`} role="listbox">
          {carregando && <div style={{ padding: "8px 12px" }}>Buscando endereços...</div>}
          {!carregando && sugestoes.map((s, i) => (
            <div
              key={s.id}
              id={`${id}-sugestao-${i}`}
              role="option"
              aria-selected={i === ativo}
              className={i === ativo ? "suggestion-item--active" : "suggestion-item"}
              style={i === ativo ? { background: "#F1F6F2" } : undefined}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setAtivo(i)}
              onClick={() => escolher(s)}
            >
              <img src={location_icon} alt="" />
              <span>{s.descricao}</span>
            </div>
          ))}
          {!carregando && aviso && (
            <div role="status" style={{ padding: "8px 12px", fontSize: 13, color: "#5f6b63" }}>{aviso}</div>
          )}
        </div>
      )}
    </div>
  );
}

export default BuscaEndereco;
